import React from 'react';
import { CopilotKit } from '@copilotkit/react-core';
import { useAuthentication } from 'hooks/api/useAuthentication';

type CopilotContextType = {
  isAvailable: boolean;
};

const CopilotContext = React.createContext<CopilotContextType>({
  isAvailable: false,
});

export function useCopilot() {
  return React.useContext(CopilotContext);
}

type CopilotProviderProps = React.PropsWithChildren;

export function CopilotProvider({ children }: CopilotProviderProps) {
  const runtimeUrl =
    import.meta.env.VITE_COPILOT_URL ||
    `${import.meta.env.VITE_API_URL}/services/copilot`;
  const [isRuntimeAvailable, setIsRuntimeAvailable] = React.useState<
    boolean | null
  >(null);

  const { authToken } = useAuthentication();

  React.useEffect(() => {
    if (!runtimeUrl) {
      setIsRuntimeAvailable(false);
      return;
    }

    // Check if runtime is available by hitting the health endpoint
    const healthUrl = runtimeUrl.replace(
      '/services/copilot',
      '/services/copilot/health'
    );
    fetch(healthUrl)
      .then((res) => {
        setIsRuntimeAvailable(res.ok);
      })
      .catch(() => {
        setIsRuntimeAvailable(false);
      });
  }, [runtimeUrl]);

  const contextValue = React.useMemo(
    () => ({ isAvailable: isRuntimeAvailable === true }),
    [isRuntimeAvailable]
  );

  // If no copilot URL is configured or runtime is not available, just render children
  if (!runtimeUrl || isRuntimeAvailable === false) {
    return (
      <CopilotContext.Provider value={contextValue}>
        {children}
      </CopilotContext.Provider>
    );
  }

  // While checking runtime availability, render children without CopilotKit
  if (isRuntimeAvailable === null || !authToken) {
    return (
      <CopilotContext.Provider value={contextValue}>
        {children}
      </CopilotContext.Provider>
    );
  }

  return (
    <CopilotContext.Provider value={contextValue}>
      <CopilotKit
        runtimeUrl={runtimeUrl}
        headers={{ Authorization: `Bearer ${authToken}` }}
      >
        {children}
      </CopilotKit>
    </CopilotContext.Provider>
  );
}
